import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, ViewChild } from '@angular/core';

import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { Observable, Subject, defer, filter, map, of, shareReplay, takeUntil } from 'rxjs';

import { HeaderUserService } from '@hdms/shared/page-frame/data-access';
import {
  MetaMenuType,
  MetaNavItem,
  PrimaryNavItem,
  SecondaryNavItem,
  UserAccountData,
  UserAccountMenuType
} from '@hdms/shared/page-frame/types';
import { LeftCardComponent, LeftCardContentComponent } from '@hdms/shared/ui-core';
import { PlatformService } from '@hdms/shared/util-core';

import { AccountLinksService, MenuStateService, accountLinkSectionMenuType } from '../../../services';
import { HeaderUserAccountWrapperComponent } from '../../header-user-account-wrapper/header-user-account-wrapper.component';

interface ActiveState {
  selectedItem: PrimaryNavItem | MetaNavItem;
  selectedChild: SecondaryNavItem;
}

export enum MenuCards {
  PRIMARY_CONTENT,
  SECONDARY_CONTENT
}

@Component({
  selector: 'shrd-pf-ft-burger-menu-wrapper',
  templateUrl: './burger-menu-wrapper.component.html',
  styleUrls: ['./burger-menu-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BurgerMenuWrapperComponent implements AfterViewInit, OnDestroy {
  @Input() primaryNavItems: PrimaryNavItem[];
  @Input() metaNavItems: MetaNavItem[];
  @Input() userData: UserAccountData;

  @ViewChild('cardsContainer') cardsContainer: LeftCardComponent;
  @ViewChild('primaryContentCard') primaryContentCard: LeftCardContentComponent;
  @ViewChild('secondaryContentCard') secondaryContentCard: LeftCardContentComponent;
  @ViewChild('userAccountWrapper') userAccountWrapper: HeaderUserAccountWrapperComponent;

  readonly isBrowser = this.platform.isBrowser;
  readonly menuCards = MenuCards;
  activeState: ActiveState;
  currentCardId = MenuCards.PRIMARY_CONTENT;
  showAccountInfo: boolean;
  showLogoutOnBottom = true;
  readonly navItems$ = this.getNavItems$();

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly stateService: MenuStateService<MetaMenuType | string>,
    private readonly headerUserService: HeaderUserService,
    private readonly accountLinksService: AccountLinksService,
    @Inject(I18NEXT_SERVICE) private readonly i18next: ITranslationService,
    private readonly platform: PlatformService
  ) {}

  ngAfterViewInit(): void {
    this.restoreMenuState();
    this.subscribeToMenuStateChange();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  activateMenuItem(menuItem: PrimaryNavItem): void {
    this.activeState = {
      selectedItem: { ...menuItem },
      selectedChild: null
    };
    this.triggerNextState(menuItem);
    this.openSecondaryContentCard();
  }

  openMenu(): void {
    this.showAccountInfo = false;
    this.showLogoutOnBottom = this.userData.isRegistered;
    this.cardsContainer.open();
    this.openPrimaryContentCard();
  }

  openMenuFromAccount(): void {
    this.showAccountInfo = true;
    this.showLogoutOnBottom = false;
    this.cardsContainer.open();
    this.openPrimaryContentCard();
  }

  openSecondaryMenu(menuItem: MetaNavItem): void {
    this.openMenu();
    this.activateMenuItem(menuItem as PrimaryNavItem);
  }

  openAccountSelectionCard(): void {
    this.activateMenuItem({
      title: this.i18next.t('header.account.select'),
      menuType: UserAccountMenuType.ACCOUNT_SELECT
    } as PrimaryNavItem);
    this.userAccountWrapper.showAccountList = false;
  }

  onAccountChanged(selectedAccountId: string): void {
    this.headerUserService.setSelectedAccount(selectedAccountId);
    this.cardsContainer.close();
  }

  closeContentCard(): void {
    this.resetServiceState();
    this.secondaryContentCard.close();
    this.resetCurrentCardId();
  }

  resetServiceState(): void {
    this.stateService.setCurrentMenuId(null);
  }

  resetCards(): void {
    this.secondaryContentCard.close();
    this.primaryContentCard.reset();
    this.secondaryContentCard.reset();
    this.resetCurrentCardId();
  }

  private getNavItems$(): Observable<PrimaryNavItem[]> {
    const accountItems$ = this.getAccountPrimaryNavItems$();
    return defer(() => {
      if (this.showAccountInfo) {
        return this.userData.accountMenuEnabled ? accountItems$ : of([]);
      } else {
        return of(this.primaryNavItems);
      }
    });
  }

  private getAccountPrimaryNavItems$(): Observable<PrimaryNavItem[]> {
    return this.accountLinksService.accountLinks$.pipe(
      map(links =>
        links.map(({ id, title }) => ({
          id: `header-account-${id}`,
          menuType: accountLinkSectionMenuType[id],
          title
        }))
      ),
      shareReplay(1)
    );
  }

  private subscribeToMenuStateChange(): void {
    this.stateService.close$.pipe(takeUntil(this.unsubscribe$)).subscribe(_ => this.cardsContainer.close());
    this.stateService.currentMenuId$
      .pipe(
        filter(id => id === MetaMenuType.USER_ACCOUNT),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(_ => {
        this.openMenuFromAccount();
        this.closeContentCard();
      });
  }

  private restoreMenuState(): void {
    const openedMenuType = this.stateService.currentMenuId;
    const openedNavItem = this.primaryNavItems?.find(item => item.id === openedMenuType);
    if (openedMenuType && openedNavItem) {
      this.openMenu();
      this.activateMenuItem(openedNavItem);
    }
  }

  private openPrimaryContentCard(): void {
    this.primaryContentCard.open();
    this.resetCurrentCardId();
  }

  private triggerNextState(menuItem: PrimaryNavItem): void {
    this.stateService.setCurrentMenuId(menuItem.id);
  }

  private openSecondaryContentCard(): void {
    this.secondaryContentCard.open();
    this.currentCardId = MenuCards.SECONDARY_CONTENT;
  }

  private resetCurrentCardId(): void {
    this.currentCardId = MenuCards.PRIMARY_CONTENT;
  }
}
